import { ThemeEvents } from '@theme/events';

export function deliveryState({ available, requiresShipping, stock, quantity, cartQuantity = 0, policy }) {
  if (!available) return 'unavailable';
  if (!requiresShipping) return 'digital';
  if (stock === null || !Number.isFinite(stock) || cartQuantity === null) return 'unknown';
  if (stock >= quantity + cartQuantity) return 'fast';
  return policy === 'deny' ? 'unavailable' : 'standard';
}

let pendingCart;
function readCart() {
  if (!pendingCart) {
    pendingCart = fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Cart delivery data unavailable');
        return response.json();
      })
      .finally(() => { pendingCart = undefined; });
  }
  return pendingCart;
}

class ArcProductDelivery extends HTMLElement {
  #controller;
  #scope;
  #variantPending = false;
  #cartQuantity = 0;
  #cartRequest = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#scope = this.closest('.shopify-section, dialog');
    const { signal } = this.#controller;
    this.#scope?.addEventListener(ThemeEvents.variantSelected, this.#onSelected, { signal });
    this.#scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariant, { signal });
    this.#scope?.addEventListener('variant:load-error', this.#onError, { signal });
    this.#scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });
    this.#scope?.addEventListener('change', this.#onQuantity, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCart, { signal });
    document.addEventListener('arc:app-cart-updated', this.#onCart, { signal });
    document.addEventListener('arc:app-cart-pending', this.#onAppCartPending, { signal });
    document.addEventListener('arc:app-cart-error', this.#onAppCartError, { signal });
    this.#cartQuantity = Number(this.dataset.cartQuantity) || 0;
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#cartRequest++;
  }

  #isRelevant(event) {
    const productId = event.detail?.data?.productId || event.target?.dataset?.productId;
    return !productId || productId === this.dataset.productId || Boolean(event.detail?.data?.newProduct);
  }

  #onSelected = (event) => {
    if (!this.#isRelevant(event)) return;
    this.#variantPending = true;
    this.#show('loading');
  };

  #onError = (event) => {
    if (this.#isRelevant(event)) this.#show('error');
  };

  #onVariant = (event) => {
    if (!this.#isRelevant(event)) return;
    const next = Array.from(event.detail.data.html.querySelectorAll('arc-product-delivery'))
      .find((element) => element.dataset.blockId === this.dataset.blockId);
    if (!next) {
      this.#show('error');
      return;
    }
    for (const key of ['variantId', 'productId', 'stock', 'available', 'shipping', 'policy', 'cartQuantity', 'defaultQuantity', 'country']) {
      this.dataset[key] = next.dataset[key] || '';
    }
    this.#cartRequest++;
    this.#cartQuantity = Number(this.dataset.cartQuantity) || 0;
    this.#variantPending = false;
    this.#render();
  };

  #form() {
    return Array.from(this.#scope?.querySelectorAll('product-form-component') || [])
      .find((element) => element.dataset.productId === this.dataset.productId);
  }

  #onQuantity = (event) => {
    if (event.target instanceof Element && this.#form()?.contains(event.target)) this.#render();
  };

  #onAppCartPending = () => {
    this.#cartRequest++;
    this.#cartQuantity = null;
    if (!this.#variantPending) this.#show('loading');
  };

  #onAppCartError = () => {
    this.#cartRequest++;
    this.#cartQuantity = null;
    if (!this.#variantPending) this.#show('unknown');
  };

  #onCart = async (event) => {
    const request = ++this.#cartRequest;
    const cart = event.detail?.resource;
    this.#cartQuantity = null;
    if (!this.#variantPending) this.#show('loading');
    try {
      const completeCart = Array.isArray(cart?.items) && Number.isFinite(cart.item_count)
        && cart.items.reduce((sum, item) => sum + item.quantity, 0) === cart.item_count;
      const updated = completeCart ? cart : await readCart();
      if (!this.isConnected || request !== this.#cartRequest) return;
      this.#cartQuantity = updated.items.reduce((sum, item) =>
        sum + (String(item.variant_id) === this.dataset.variantId ? item.quantity : 0), 0);
      this.#render();
    } catch {
      if (this.isConnected && request === this.#cartRequest) this.#show('unknown');
    }
  };

  #render() {
    if (this.#variantPending) return;
    const input = this.#form()?.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, Number(input?.value) || Number(this.dataset.defaultQuantity) || 1);
    this.#show(deliveryState({
      available: this.dataset.available === 'true',
      requiresShipping: this.dataset.shipping !== 'false',
      stock: this.dataset.stock.trim() === '' ? null : Number(this.dataset.stock),
      quantity,
      policy: this.dataset.policy,
      cartQuantity: this.#cartQuantity,
    }));
  }

  #show(state) {
    const strings = Theme.translations.arc_delivery;
    const panel = this.querySelector('.arc-delivery');
    const label = this.querySelector('[data-arc-stock-label]');
    const message = this.querySelector('[data-arc-delivery-message]');
    if (!panel || !label || !message) return;
    panel.dataset.state = state;
    panel.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    label.textContent = state === 'fast' ? strings.in_helsinki : state === 'standard' ? strings.orderable : '';
    const domesticOnly = ['fast', 'standard'].includes(state);
    const key = domesticOnly && this.dataset.country !== 'FI' ? 'international' : state;
    message.textContent = strings[key] || strings.unknown;
  }
}

if (!customElements.get('arc-product-delivery')) customElements.define('arc-product-delivery', ArcProductDelivery);
